import { parseImportDate } from '../schedule/import-parsing';
import {
  cellReader,
  parseImportMoney,
  rowNumberOf,
  summarizeRows,
  suggestMapping,
  type ColumnMappingOf,
  type ImportColumnDef,
  type ImportIssueOf,
  type ImportRowStatus,
  type ImportSummary,
} from '../imports/table-mapping';
import { normalizeName } from '../shared/naming';
import type { ProjectId } from '../shared/ids';
import { Cpf } from './cpf';
import {
  EMPLOYEE_CHARGES_MAX_PERCENT,
  employeeCpfKey,
  employeeRegistrationKey,
  type Employee,
} from './employee';
import { jobRoleNaturalKey, teamNaturalKey, type JobRole, type Team } from './team';

/**
 * Importação da equipe por planilha.
 *
 * O cadastro de quem trabalha na obra já existe em algum lugar — na folha, no controle do
 * escritório, na planilha do encarregado. Redigitar cinquenta pessoas para começar a usar
 * o sistema é a barreira que faz um sistema bom não ser adotado, e é ela que esta
 * importação remove.
 *
 * Tudo aqui é **puro**: recebe as linhas já lidas do arquivo e devolve o que aconteceria
 * com cada uma. Nada toca o banco antes da confirmação (RN-030).
 *
 * A identidade segue o cadastro, não o arquivo: **CPF** primeiro (RN-050), **matrícula**
 * depois (RN-051). Quem tem os dois vazios é sempre gente nova — o diarista sem CPF
 * cadastrado duas vezes é o preço de não inventar identidade a partir do nome, que
 * juntaria dois "José da Silva" diferentes num só.
 */

export type EmployeeImportField =
  | 'name'
  | 'jobRole'
  | 'cpf'
  | 'registration'
  | 'monthlySalary'
  | 'chargesPercent'
  | 'admittedAt'
  | 'phone'
  | 'team'
  | 'notes';

export const EMPLOYEE_IMPORT_COLUMNS: readonly ImportColumnDef<EmployeeImportField>[] = [
  {
    field: 'name',
    label: 'Nome',
    required: true,
    synonyms: ['nome', 'nome completo', 'funcionario', 'colaborador', 'empregado', 'trabalhador'],
  },
  {
    field: 'jobRole',
    label: 'Função',
    required: true,
    synonyms: ['funcao', 'cargo', 'ocupacao', 'profissao', 'atividade', 'cbo', 'funcao cargo'],
  },
  {
    field: 'cpf',
    label: 'CPF',
    required: false,
    synonyms: ['cpf', 'documento', 'doc', 'cpf mf', 'n cpf'],
  },
  {
    field: 'registration',
    label: 'Matrícula',
    required: false,
    synonyms: ['matricula', 'registro', 'chapa', 'codigo', 'cod', 'id', 'cracha'],
  },
  {
    field: 'monthlySalary',
    label: 'Salário mensal',
    required: false,
    synonyms: [
      'salario',
      'salario mensal',
      'salario base',
      'remuneracao',
      'vencimento',
      'valor',
      'folha',
    ],
  },
  {
    // Fase 29, D12: o encargo é por funcionário, e a folha do escritório costuma trazê-lo ao
    // lado do salário. `headerKey` tira o "%" e os parênteses — "Encargos (%)", "% encargos" e
    // "encargos %" chegam aqui como "encargos".
    field: 'chargesPercent',
    label: 'Encargos (%)',
    required: false,
    synonyms: [
      'encargos',
      'encargo',
      'encargos sociais',
      'encargo social',
      'percentual de encargos',
      'percentual encargos',
      'perc encargos',
      'encargos sobre salario',
      'encargos sobre o salario',
    ],
  },
  {
    field: 'admittedAt',
    label: 'Admissão',
    required: false,
    synonyms: ['admissao', 'data de admissao', 'data admissao', 'entrada', 'inicio', 'contratacao'],
  },
  {
    field: 'phone',
    label: 'Telefone',
    required: false,
    synonyms: ['telefone', 'fone', 'celular', 'contato', 'whatsapp', 'tel'],
  },
  {
    field: 'team',
    label: 'Equipe',
    required: false,
    synonyms: ['equipe', 'time', 'turma', 'frente', 'grupo', 'setor'],
  },
  {
    field: 'notes',
    label: 'Observação',
    required: false,
    synonyms: ['observacao', 'obs', 'observacoes', 'anotacao', 'comentario'],
  },
];

export type EmployeeColumnMapping = ColumnMappingOf<EmployeeImportField>;
export type EmployeeImportIssue = ImportIssueOf<EmployeeImportField>;

export function suggestEmployeeMapping(headers: readonly string[]): EmployeeColumnMapping {
  return suggestMapping(headers, EMPLOYEE_IMPORT_COLUMNS);
}

export interface EmployeeImportRow {
  readonly rowNumber: number;
  readonly status: ImportRowStatus;
  readonly name: string;
  /** Texto da função como veio no arquivo — a tela mostra o que não foi reconhecido. */
  readonly jobRoleLabel: string;
  /** Resolvido contra o catálogo de funções; ausente = função desconhecida. */
  readonly jobRoleId: string | undefined;
  /** Só dígitos, como o cadastro guarda (RN-050). */
  readonly cpf: string | undefined;
  readonly registration: string | undefined;
  /** Em reais; o caso de uso converte para centavos. Ausente = sem salário (RN-055). */
  readonly monthlySalary: number | undefined;
  /**
   * Os encargos sobre o salário, em % (Fase 29, D12), com duas casas — as que o cadastro guarda.
   *
   * Ausente = a planilha não diz, e a importação **não apaga** o encargo que já está no
   * cadastro. Diferente do salário, que a planilha substitui: o encargo nasce na tela de mão de
   * obra, e reimportar a folha de antes dele não pode zerar o que o escritório acabou de
   * preencher. Para tirar um encargo, a tela.
   */
  readonly chargesPercent: number | undefined;
  readonly admittedAt: string | undefined;
  readonly phone: string | undefined;
  readonly teamLabel: string;
  /** Equipe existente com esse nome; ausente com `teamLabel` preenchido = equipe nova. */
  readonly teamId: string | undefined;
  readonly notes: string | undefined;
  /** Funcionário já cadastrado, por CPF ou matrícula. */
  readonly existingId: string | undefined;
  readonly issues: readonly EmployeeImportIssue[];
}

/**
 * O encargo escrito numa planilha (Fase 29, D12): `"80"`, `"80%"`, `"80,5"` e `"80.5"`.
 *
 * O leitor de XLSX já devolve a célula formatada como porcentagem escrita com `%` (`0,8` vira
 * `"80%"`), e é por isso que o `%` só se tira aqui. O número sai com as duas casas que o
 * cadastro guarda: com três, a segunda importação compararia `80,555` com o `80,56` gravado e
 * diria "atualiza" para sempre.
 *
 * Devolve `undefined` para a célula vazia — encargo não informado é estado legítimo — e `null`
 * para o que não é um percentual de 0 a 300: o negativo, o acima do teto e o texto.
 */
export function parseImportChargesPercent(raw: string): number | undefined | null {
  const valor = parseImportMoney(raw.replace(/%/g, ''));
  if (valor === undefined || valor === null) return valor;
  if (valor > EMPLOYEE_CHARGES_MAX_PERCENT) return null;
  return Math.round(valor * 100) / 100;
}

export interface ClassifyEmployeeImportInput {
  readonly rows: readonly Record<string, string>[];
  readonly mapping: EmployeeColumnMapping;
  readonly projectId: ProjectId;
  readonly roles: readonly JobRole[];
  readonly teams: readonly Team[];
  readonly existing: readonly Employee[];
  /**
   * Criar a função que a planilha traz e o catálogo não tem.
   *
   * Desligado, a linha é recusada com a função escrita na tela — o que evita que um
   * "Pedrero" digitado errado vire uma função nova para sempre. Ligado, o escritório
   * assume que a planilha é a fonte.
   */
  readonly createMissingRoles?: boolean;
}

export interface EmployeeImportPlan {
  readonly rows: readonly EmployeeImportRow[];
  readonly summary: ImportSummary;
  /** Funções citadas no arquivo que não existem no catálogo, sem repetição. */
  readonly unknownRoles: readonly string[];
  /** Equipes citadas no arquivo que ainda não existem na obra. */
  readonly newTeams: readonly string[];
}

/**
 * Classifica cada linha do arquivo.
 *
 * O que decide o destino de uma linha:
 *
 * - sem nome ou sem função → `INVALID`; são o mínimo para alguém existir no planejamento;
 * - CPF inválido → `INVALID`, com o mesmo validador do formulário (RN-050) — importar não
 *   é uma porta dos fundos para dado que a tela recusaria;
 * - CPF ou matrícula repetidos **dentro do arquivo** → `DUPLICATE`, e vale a primeira;
 * - já cadastrado (por CPF ou matrícula) → `UPDATE`, ou `UNCHANGED` se nada mudaria;
 * - o resto → `CREATE`.
 *
 * Nenhuma linha ruim derruba as demais: planilha do mundo real vem com sujeira, e recusar
 * as cinquenta por causa de três seria trocar um problema pequeno por um grande.
 */
export function classifyEmployeeImport(input: ClassifyEmployeeImportInput): EmployeeImportPlan {
  const roleByKey = new Map(input.roles.map((role) => [jobRoleNaturalKey(role.name), role]));
  const teamByKey = new Map(
    input.teams.map((team) => [teamNaturalKey(input.projectId, team.name), team]),
  );
  const byCpf = new Map(
    input.existing
      .filter((employee) => employee.cpf !== undefined)
      .map((employee) => [employeeCpfKey(input.projectId, employee.cpf!), employee]),
  );
  const byRegistration = new Map(
    input.existing
      .filter((employee) => employee.registration !== undefined)
      .map((employee) => [
        employeeRegistrationKey(input.projectId, employee.registration!),
        employee,
      ]),
  );

  const seenCpf = new Set<string>();
  const seenRegistration = new Set<string>();
  const unknownRoles = new Set<string>();
  const newTeams = new Set<string>();
  const rows: EmployeeImportRow[] = [];

  input.rows.forEach((raw, index) => {
    const pick = cellReader(raw, input.mapping);
    const issues: EmployeeImportIssue[] = [];

    const name = pick('name');
    const jobRoleLabel = pick('jobRole');
    const cpfRaw = pick('cpf');
    const registration = pick('registration');
    const salaryRaw = pick('monthlySalary');
    const chargesRaw = pick('chargesPercent');
    const admittedRaw = pick('admittedAt');
    const phone = pick('phone');
    const teamLabel = pick('team');
    const notes = pick('notes');

    if (name === '') issues.push({ field: 'name', message: 'Nome vazio.' });

    const role = roleByKey.get(jobRoleNaturalKey(jobRoleLabel));
    if (jobRoleLabel === '') {
      issues.push({ field: 'jobRole', message: 'Função vazia.' });
    } else if (role === undefined) {
      unknownRoles.add(jobRoleLabel);
      if (input.createMissingRoles !== true) {
        issues.push({
          field: 'jobRole',
          message: `Função "${jobRoleLabel}" não existe. Marque para criar as funções novas.`,
        });
      }
    }

    // O mesmo validador do formulário: importar não afrouxa a regra (RN-050).
    let cpf: string | undefined;
    if (cpfRaw !== '') {
      if (Cpf.isValid(cpfRaw)) {
        cpf = cpfRaw.replace(/\D/g, '');
      } else {
        issues.push({ field: 'cpf', message: `CPF "${cpfRaw}" inválido.` });
      }
    }

    const salary = parseImportMoney(salaryRaw);
    if (salary === null) {
      issues.push({ field: 'monthlySalary', message: `Salário "${salaryRaw}" não é um valor.` });
    }

    const charges = parseImportChargesPercent(chargesRaw);
    if (charges === null) {
      issues.push({
        field: 'chargesPercent',
        message: `Encargo "${chargesRaw}" não é um percentual de 0 a ${String(EMPLOYEE_CHARGES_MAX_PERCENT)}%.`,
      });
    }

    let admittedAt: string | undefined;
    if (admittedRaw !== '') {
      const parsed = parseImportDate(admittedRaw);
      if (parsed.ok) admittedAt = parsed.value;
      else issues.push({ field: 'admittedAt', message: `Data "${admittedRaw}" não reconhecida.` });
    }

    const team =
      teamLabel === '' ? undefined : teamByKey.get(teamNaturalKey(input.projectId, teamLabel));
    if (teamLabel !== '' && team === undefined) newTeams.add(teamLabel);

    // Repetição dentro do arquivo: duas linhas com o mesmo CPF disputariam o índice único.
    const cpfKey = cpf === undefined ? undefined : employeeCpfKey(input.projectId, cpf);
    const registrationKey =
      registration === '' ? undefined : employeeRegistrationKey(input.projectId, registration);

    const repeated =
      (cpfKey !== undefined && seenCpf.has(cpfKey)) ||
      (registrationKey !== undefined && seenRegistration.has(registrationKey));

    if (cpfKey !== undefined) seenCpf.add(cpfKey);
    if (registrationKey !== undefined) seenRegistration.add(registrationKey);

    const existing =
      (cpfKey === undefined ? undefined : byCpf.get(cpfKey)) ??
      (registrationKey === undefined ? undefined : byRegistration.get(registrationKey));

    let status: ImportRowStatus;
    if (issues.length > 0) {
      status = 'INVALID';
    } else if (repeated) {
      status = 'DUPLICATE';
      issues.push({ field: 'row', message: 'CPF ou matrícula repetidos dentro do arquivo.' });
    } else if (existing !== undefined) {
      const changed =
        normalizeName(existing.name) !== normalizeName(name) ||
        (role !== undefined && existing.jobRoleId !== role.id) ||
        (existing.registration ?? '') !== registration ||
        (existing.monthlySalary === undefined ? undefined : existing.monthlySalary.cents) !==
          (salary === undefined || salary === null ? undefined : Math.round(salary * 100)) ||
        // O encargo em branco não muda nada (ele não apaga o do cadastro); só o escrito compara.
        (charges !== undefined && charges !== null && existing.chargesPercent !== charges) ||
        (existing.admittedAt ?? undefined) !== admittedAt ||
        (existing.phone ?? '') !== phone ||
        (existing.notes ?? '') !== notes;
      status = changed ? 'UPDATE' : 'UNCHANGED';
    } else {
      status = 'CREATE';
    }

    rows.push({
      rowNumber: rowNumberOf(index),
      status,
      name,
      jobRoleLabel,
      jobRoleId: role?.id,
      cpf,
      registration: registration === '' ? undefined : registration,
      monthlySalary: salary === null ? undefined : salary,
      chargesPercent: charges === null ? undefined : charges,
      admittedAt,
      phone: phone === '' ? undefined : phone,
      teamLabel,
      teamId: team?.id,
      notes: notes === '' ? undefined : notes,
      existingId: existing?.id,
      issues,
    });
  });

  return {
    rows,
    summary: summarizeRows(rows),
    unknownRoles: [...unknownRoles],
    newTeams: [...newTeams],
  };
}
